import React, { useEffect, useState, useMemo } from "react";
import {
  computeSavings,
  FRAME_A_DS_LEN_M,
  FRAME_A_CP_LEN_M,
  FRAME_B_DS_LEN_M,
  FRAME_B_CP_LEN_M,
} from "@/lib/simulation-defaults";
import { getSettings } from "@/lib/api";
import {
  makePdfT,
  pdfFmt,
  periodLabelKey,
  validatePdfTranslations,
} from "@/i18n/pdfI18n";

/**
 * PdfTechnicalReport
 * -----------------------------------------------------------------
 * Dense §1-§5 technical report variant.
 *
 * Uses the dedicated PDF i18n system (`@/i18n/pdfI18n`). The
 * `overrideLang` prop is MANDATORY — the PDF is always rendered in
 * that specific language and no key is silently resolved through
 * another language. All numbers, currency and dates are formatted
 * with the locale mapped from `overrideLang`.
 * -----------------------------------------------------------------
 */

export default function PdfTechnicalReport({ state, overrideLang, logoDataUrl }) {
  // ---- Language (sovereign) ----
  const lang = overrideLang;
  if (!lang) {
    // Fail loudly during development if the parent forgot to pass a language.
    // eslint-disable-next-line no-console
    console.error(
      "[PdfTechnicalReport] `overrideLang` is required and must be one of the supported PDF languages."
    );
  }
  // Validate the selected language once; log any missing keys.
  useMemo(() => {
    if (!lang) return null;
    const v = validatePdfTranslations(lang);
    if (!v.ok) {
      // eslint-disable-next-line no-console
      console.error(
        `[PdfTechnicalReport] Missing PDF translations for language "${lang}":`,
        v.missing
      );
    }
    return v;
  }, [lang]);

  const tPdf = useMemo(() => (lang ? makePdfT(lang) : () => ""), [lang]);
  const fmt = useMemo(
    () => ({
      money: (v) => pdfFmt.money(v, lang),
      money0: (v) => pdfFmt.money0(v, lang),
      percent: (v, d = 1) => pdfFmt.percent(v, lang, d),
      hours: (v) => pdfFmt.hours(v, lang),
      minAndHours: (v) => pdfFmt.minAndHours(v, lang),
      date: (d) => pdfFmt.date(d, lang),
      num: (v, o) => pdfFmt.number(v, lang, o),
    }),
    [lang]
  );

  // ---- Data ----
  const { client, trailer, customer, primo } = state;
  const thickness = Number(trailer.profile_thickness_mm) || 85;
  const s = computeSavings(state);
  const { cb, pb, ct, pt, doors } = s;

  const priceCurrent = s.priceCustomer;
  const pricePrimo = s.pricePrimo;
  const priceDelta = s.priceSavings;
  const reductionPct = s.priceReductionPct;
  const timeCurrentMin = s.timeCustomerMin;
  const timePrimoMin = s.timePrimoMin;
  const timeDeltaMin = s.timeSavingsMin;
  const timeDeltaH = s.timeSavingsH;
  const timeReductionPct = s.timeReductionPct;
  const quantity = s.quantity;
  const period = s.period;
  const periodLabel = tPdf(periodLabelKey(period, "label"));
  const periodLower = tPdf(periodLabelKey(period, "lower"));
  const periodSavings = s.periodSavings;
  const annualSavings = s.annualSavings;
  const periodTimeH = s.periodTimeH;
  const annualTimeH = s.annualTimeH;

  const refId = state.id ? state.id.slice(0, 8).toUpperCase() : "DRAFT";

  const [techSettings, setTechSettings] = useState({
    tech_name: "",
    tech_email: "",
    tech_phone: "",
    tech_greeting: "",
    tech_signature_image: "",
  });
  useEffect(() => {
    getSettings().then(setTechSettings).catch(() => {});
  }, []);
  const hasTech =
    techSettings.tech_name ||
    techSettings.tech_email ||
    techSettings.tech_phone ||
    techSettings.tech_signature_image;

  const custCutting = (Number(customer.cutting_cost) || 0) * doors;
  const custGluing = (Number(customer.corner_gluing_cost) || 0) * doors;
  const custMounting = (Number(customer.mounting_cost) || 0) * doors;
  const primoWelding = (Number(primo.welding_cutting_cost) || 0) * doors;
  const primoGluing = (Number(primo.gluing_cost) || 0) * doors;
  const primoMounting = (Number(primo.mounting_cost) || 0) * doors;
  const primoGlue = pb.materials;
  const custSteel = cb.steelband;

  const custCuttingMin = ct.cutting * doors;
  const custGluingMin = ct.gluing * doors;
  const custMountingMin = ct.mounting * doors;
  const primoWeldingMin = pt.welding * doors;
  const primoGluingMin = pt.gluing * doors;
  const primoMountingMin = pt.mounting * doors;

  const logoSrc = logoDataUrl || "/brand/primo-logo.png";
  const doorsNoun = tPdf("doors", { count: doors });
  const setsNoun = tPdf("sets", { count: quantity });

  return (
    <div className="pdf-doc">
      <div className="pdf-accent-bar" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "3mm", gap: "10mm" }}>
        <div>
          <img src={logoSrc} alt={tPdf("logoAlt")} style={{ height: "9mm", width: "auto", display: "block" }} />
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "7.5pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#6b7280", fontFamily: "JetBrains Mono, monospace" }}>
            {tPdf("headerTitle")}
          </div>
          <div style={{ fontSize: "13pt", fontWeight: 700, fontFamily: "Space Grotesk, sans-serif", marginTop: "0.5mm" }}>
            {tPdf("headerSubtitle")}
          </div>
          <div style={{ fontSize: "8.5pt", color: "#6b7280", fontFamily: "JetBrains Mono, monospace", marginTop: "1mm" }}>
            {tPdf("refLabel")} {refId} · {fmt.date(new Date())}
          </div>
        </div>
      </div>

      {/* §1 Client */}
      <div className="pdf-section" style={{ marginTop: "5mm" }}>
        <div className="pdf-section-title">{tPdf("s1")}</div>
        <div className="pdf-kv-grid">
          <div><div className="pdf-kv-label">{tPdf("client")}</div><div className="pdf-kv-value">{client.company_name || "—"}</div></div>
          <div><div className="pdf-kv-label">{tPdf("contact")}</div><div className="pdf-kv-value">{client.contact_name || "—"}</div></div>
          <div><div className="pdf-kv-label">{tPdf("email")}</div><div className="pdf-kv-value" style={{ fontSize: "9pt", wordBreak: "break-all" }}>{client.contact_email || "—"}</div></div>
          <div><div className="pdf-kv-label">{tPdf("production")}</div><div className="pdf-kv-value">{fmt.num(quantity)} <span style={{ fontSize: "8pt", color: "#6b7280", fontWeight: 400 }}>/ {periodLower}</span></div></div>
          <div><div className="pdf-kv-label">{tPdf("doorHeight")}</div><div className="pdf-kv-value">{fmt.num(trailer.height_mm)} mm</div></div>
          <div><div className="pdf-kv-label">{tPdf("doorWidth")}</div><div className="pdf-kv-value">{fmt.num(trailer.width_mm)} mm</div></div>
          <div><div className="pdf-kv-label">{tPdf("doorsPerTrailer")}</div><div className="pdf-kv-value">{fmt.num(doors)}</div></div>
          <div><div className="pdf-kv-label">{tPdf("profile")}</div><div className="pdf-kv-value">{fmt.num(thickness)} mm</div></div>
        </div>
        {client.notes && (
          <div style={{ marginTop: "3mm", fontSize: "8.5pt", color: "#374151", borderLeft: "2px solid #00B2FF", paddingLeft: "3mm" }}>
            <span className="pdf-kv-label" style={{ marginRight: "2mm" }}>{tPdf("notes")}</span>{client.notes}
          </div>
        )}
      </div>

      {/* §2 Door layout */}
      <div className="pdf-section">
        <div className="pdf-section-title">{tPdf("s2")} ({fmt.num(doors)} {doorsNoun})</div>
        <div className="pdf-kv-grid two-col" style={{ gap: "4mm 6mm" }}>
          <div style={{ border: "1px solid #d4d4d4", padding: "3mm 4mm", borderRadius: "1.5mm" }}>
            <div className="pdf-kv-label" style={{ color: "#00B2FF" }}>◄ {tPdf("leftDoor")}</div>
            <div style={{ fontSize: "9pt", marginTop: "1.5mm", color: "#0a0a0a" }}>
              {tPdf("leftDoorDesc", { t: thickness })}
            </div>
            <table className="pdf-table" style={{ marginTop: "2mm" }}><tbody>
              <tr><td>{tPdf("dsLength")}</td><td className="num">{fmt.num(FRAME_A_DS_LEN_M, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td></tr>
              <tr><td>{tPdf("cpLength")}</td><td className="num">{fmt.num(FRAME_A_CP_LEN_M, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td></tr>
            </tbody></table>
          </div>
          <div style={{ border: "1px solid #d4d4d4", padding: "3mm 4mm", borderRadius: "1.5mm" }}>
            <div className="pdf-kv-label" style={{ color: "#00B2FF" }}>{tPdf("rightDoor")} ►</div>
            <div style={{ fontSize: "9pt", marginTop: "1.5mm", color: "#0a0a0a" }}>
              {tPdf("rightDoorDesc", { t: thickness })}
            </div>
            <table className="pdf-table" style={{ marginTop: "2mm" }}><tbody>
              <tr><td>{tPdf("dsLength")}</td><td className="num">{fmt.num(FRAME_B_DS_LEN_M, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td></tr>
              <tr><td>{tPdf("cpLength")}</td><td className="num">{fmt.num(FRAME_B_CP_LEN_M, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m</td></tr>
            </tbody></table>
          </div>
        </div>
      </div>

      {/* §3 Cost */}
      <div className="pdf-section">
        <div className="pdf-section-title">{tPdf("s3")} — {tPdf("totalSet", { n: doors })}</div>
        <table className="pdf-table">
          <thead><tr>
            <th style={{ width: "42%" }}>{tPdf("component")}</th>
            <th className="num">{tPdf("customer")}</th>
            <th className="num">{tPdf("primo")}</th>
            <th className="num">{tPdf("savings")}</th>
          </tr></thead>
          <tbody>
            <tr>
              <td>{tPdf("leftDoorProfiles")}</td>
              <td className="num">{fmt.money(cb.frameAProfiles)}</td>
              <td className="num">{fmt.money(pb.frameAProfiles)}</td>
              <td className="num pdf-good">{fmt.money(cb.frameAProfiles - pb.frameAProfiles)}</td>
            </tr>
            <tr>
              <td>{tPdf("rightDoorProfiles")}</td>
              <td className="num">{fmt.money(cb.frameBProfiles)}</td>
              <td className="num">{fmt.money(pb.frameBProfiles)}</td>
              <td className="num pdf-good">{fmt.money(cb.frameBProfiles - pb.frameBProfiles)}</td>
            </tr>
            <tr>
              <td>{tPdf("cuttingWelding")} (× {fmt.num(doors)})</td>
              <td className="num">{fmt.money(custCutting)}</td>
              <td className="num">{fmt.money(primoWelding)}</td>
              <td className="num pdf-good">{fmt.money(custCutting - primoWelding)}</td>
            </tr>
            <tr>
              <td>{tPdf("cornerGluing")} (× {fmt.num(doors)})</td>
              <td className="num">{fmt.money(custGluing)}</td>
              <td className="num">{fmt.money(primoGluing)}</td>
              <td className="num pdf-good">{fmt.money(custGluing - primoGluing)}</td>
            </tr>
            <tr>
              <td>{tPdf("mounting")} (× {fmt.num(doors)})</td>
              <td className="num">{fmt.money(custMounting)}</td>
              <td className="num">{fmt.money(primoMounting)}</td>
              <td className="num pdf-good">{fmt.money(custMounting - primoMounting)}</td>
            </tr>
            <tr>
              <td>{tPdf("steelbandRow")} (× {fmt.num(doors)})</td>
              <td className="num">{fmt.money(custSteel)}</td>
              <td className="num pdf-good">{tPdf("eliminated")}</td>
              <td className="num pdf-good">{fmt.money(custSteel)}</td>
            </tr>
            <tr>
              <td>{tPdf("glueRow")} (× {fmt.num(doors)})</td>
              <td className="num">—</td>
              <td className="num">{fmt.money(primoGlue)}</td>
              <td className="num pdf-danger">−{fmt.money(primoGlue)}</td>
            </tr>
          </tbody>
          <tfoot><tr>
            <td>{tPdf("totalSet", { n: doors })}</td>
            <td className="num">{fmt.money(priceCurrent)}</td>
            <td className="num">{fmt.money(pricePrimo)}</td>
            <td className="num pdf-accent">{fmt.money(priceDelta)}</td>
          </tr></tfoot>
        </table>
      </div>

      {/* §4 Time */}
      <div className="pdf-section">
        <div className="pdf-section-title">{tPdf("s4")} — {fmt.num(doors)} {doorsNoun} (min)</div>
        <table className="pdf-table">
          <thead><tr>
            <th style={{ width: "42%" }}>{tPdf("operation")}</th>
            <th className="num">{tPdf("customer")}</th>
            <th className="num">{tPdf("primo")}</th>
            <th className="num">{tPdf("savings")}</th>
          </tr></thead>
          <tbody>
            <tr>
              <td>{tPdf("cuttingWelding")}</td>
              <td className="num">{fmt.minAndHours(custCuttingMin)}</td>
              <td className="num">{fmt.minAndHours(primoWeldingMin)}</td>
              <td className="num pdf-good">{fmt.minAndHours(custCuttingMin - primoWeldingMin)}</td>
            </tr>
            <tr>
              <td>{tPdf("cornerGluing")}</td>
              <td className="num">{fmt.minAndHours(custGluingMin)}</td>
              <td className="num">{fmt.minAndHours(primoGluingMin)}</td>
              <td className="num pdf-good">{fmt.minAndHours(custGluingMin - primoGluingMin)}</td>
            </tr>
            <tr>
              <td>{tPdf("mounting")}</td>
              <td className="num">{fmt.minAndHours(custMountingMin)}</td>
              <td className="num">{fmt.minAndHours(primoMountingMin)}</td>
              <td className="num pdf-good">{fmt.minAndHours(custMountingMin - primoMountingMin)}</td>
            </tr>
          </tbody>
          <tfoot><tr>
            <td>{tPdf("totalLabour")}</td>
            <td className="num">{fmt.minAndHours(timeCurrentMin)}</td>
            <td className="num">{fmt.minAndHours(timePrimoMin)}</td>
            <td className="num pdf-accent">{fmt.minAndHours(timeDeltaMin)} ({fmt.percent(timeReductionPct)})</td>
          </tr></tfoot>
        </table>
      </div>

      {/* §5 Summary */}
      <div className="pdf-section">
        <div className="pdf-section-title">{tPdf("s5")}</div>
        <div className="pdf-callout">
          <div>
            <div className="label">{tPdf("perSet", { n: doors })}</div>
            <div className="big">{fmt.money0(priceDelta)}</div>
            <div style={{ fontSize: "8pt", color: "#6b7280", marginTop: "1mm" }}>
              {fmt.percent(reductionPct)} {tPdf("costReduction")} · {fmt.hours(timeDeltaH)} {tPdf("savedHours")}
            </div>
          </div>
          <div>
            <div className="label">{periodLabel}</div>
            <div className="big">{fmt.money0(periodSavings)}</div>
            <div style={{ fontSize: "8pt", color: "#6b7280", marginTop: "1mm" }}>
              {fmt.num(quantity)} {setsNoun} · {fmt.hours(periodTimeH)} {tPdf("savedHours")}
            </div>
          </div>
          <div>
            <div className="label">{tPdf("annualised")}</div>
            <div className="big">{fmt.money0(annualSavings)}</div>
            <div style={{ fontSize: "8pt", color: "#6b7280", marginTop: "1mm" }}>
              {fmt.hours(annualTimeH)} {tPdf("savedHours")} {tPdf("perYear")}
            </div>
          </div>
        </div>
      </div>

      {/* Footer / signature */}
      {hasTech ? (
        <div className="pdf-footer" style={{ marginTop: "10mm", paddingTop: "4mm", borderTop: "1.5px solid #00B2FF", display: "block" }}>
          {techSettings.tech_greeting && (
            <div style={{ fontSize: "9pt", color: "#374151", marginBottom: "2mm", fontStyle: "italic" }}>{techSettings.tech_greeting}</div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "10mm" }}>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              {techSettings.tech_signature_image && (
                <div style={{ marginBottom: "2mm" }}>
                  <img
                    src={techSettings.tech_signature_image}
                    alt={tPdf("signatureAlt")}
                    style={{ maxHeight: "18mm", maxWidth: "70mm", objectFit: "contain", display: "block" }}
                  />
                </div>
              )}
              {techSettings.tech_name && <div style={{ fontSize: "10pt", fontWeight: 700, color: "#0a0a0a", fontFamily: "Space Grotesk, sans-serif" }}>{techSettings.tech_name}</div>}
              <div style={{ fontSize: "8pt", color: "#6b7280", fontFamily: "JetBrains Mono, monospace", marginTop: "1mm" }}>
                {[techSettings.tech_email, techSettings.tech_phone].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: "7.5pt", color: "#6b7280", fontFamily: "JetBrains Mono, monospace" }}>{tPdf("refLabel")} {refId} · {fmt.date(new Date())}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="pdf-footer">
          <div>{tPdf("brandLine")}</div>
          <div>{tPdf("refLabel")} {refId} · {fmt.date(new Date())}</div>
        </div>
      )}
    </div>
  );
}
